import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalDate } from '@js-joda/core';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import { i18n } from 'i18n';
import { dayJsToLocalDate, LocalDateToDayJs } from 'utils';
import {
  canShortenSpan,
  describeSpoolDateFilter,
  isSpoolDateFilterActive,
  NO_SPOOL_DATE_FILTER,
  periodAround,
  SpoolDateField,
  spoolDateFieldLabel,
  SpoolDateFilter,
  SpoolDatePeriod,
  SpanPeriod,
  stepSpanEnd,
  UndatedSpools,
  withField,
  withPeriod,
  withSpanEnd,
  withSpanStart
} from '../../MineModel/spoolDateFilter';

interface SpoolDateFilterFieldProps {
  filter: SpoolDateFilter;
  onChange: (filter: SpoolDateFilter) => void;
}

interface ChoiceProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

const Choice = <T extends string>({
  label, value, options, onChange, disabled = false,
}: ChoiceProps<T>) => (
  <Box sx={{ mt: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>{label}</Typography>
    <ToggleButtonGroup
      size="small"
      exclusive
      fullWidth
      color="secondary"
      value={value}
      disabled={disabled}
      onChange={(event, next: T | null) => next && onChange(next)}
    >
      {options.map((option) => <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>)}
    </ToggleButtonGroup>
  </Box>
);

const fieldHint = (field: SpoolDateField): string => ({
  inService: i18n._('Spools in place at any time in the period'),
  installed: i18n._('Spools installed during the period'),
  removed: i18n._('Spools removed during the period'),
})[field];

const toDayjs = (date: LocalDate | null): Dayjs | null => (date ? LocalDateToDayJs(date) : null);

// Null is a cleared field; undefined is a date still being typed, which is left alone until it is complete.
const toLocalDate = (date: Dayjs | null): LocalDate | null | undefined => {
  if (!date) return null;
  return date.isValid() ? dayJsToLocalDate(date) : undefined;
};

const SPAN_PICKERS: Record<SpanPeriod, { format: string; views: ('year' | 'month')[]; step: () => { back: string; forward: string } }> = {
  year: { format: 'YYYY', views: ['year'], step: () => ({ back: i18n._('-1 year'), forward: i18n._('+1 year') }) },
  month: { format: 'MMM YYYY', views: ['month', 'year'], step: () => ({ back: i18n._('-1 month'), forward: i18n._('+1 month') }) },
};

const pickerSlotProps = { textField: { size: 'small' as const, fullWidth: true, color: 'secondary' as const }, field: { clearable: true } };

const SpoolDateFilterField = ({ filter, onChange }: SpoolDateFilterFieldProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const active = isSpoolDateFilterActive(filter);
  const periodDisabled = filter.undated === 'only';

  const handleDayChange = (date: Dayjs | null) => {
    const picked = toLocalDate(date);
    if (picked === undefined) return;
    onChange(picked ? { ...filter, ...periodAround('day', picked) } : { ...filter, from: null, to: null });
  };

  const handleSpanEnd = (period: SpanPeriod, end: 'from' | 'to') => (date: Dayjs | null) => {
    const picked = toLocalDate(date);
    if (picked === undefined) return;
    onChange(end === 'from' ? withSpanStart(filter, period, picked) : withSpanEnd(filter, period, picked));
  };

  const handleRangeEnd = (end: 'from' | 'to') => (date: Dayjs | null) => {
    const picked = toLocalDate(date);
    if (picked === undefined) return;
    onChange({ ...filter, [end]: picked });
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(NO_SPOOL_DATE_FILTER);
  };

  const renderPeriodPicker = () => {
    if (filter.period === 'range') {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DatePicker
            format="DD/MM/YYYY"
            label={i18n._('From')}
            value={toDayjs(filter.from)}
            maxDate={toDayjs(filter.to) ?? undefined}
            disabled={periodDisabled}
            onChange={handleRangeEnd('from')}
            slotProps={pickerSlotProps}
          />
          <DatePicker
            format="DD/MM/YYYY"
            label={i18n._('To')}
            value={toDayjs(filter.to)}
            minDate={toDayjs(filter.from) ?? undefined}
            disabled={periodDisabled}
            onChange={handleRangeEnd('to')}
            slotProps={pickerSlotProps}
          />
        </Box>
      );
    }
    if (filter.period === 'day') {
      return (
        <DatePicker
          key="day"
          format="DD/MM/YYYY"
          label={i18n._('Date')}
          value={toDayjs(filter.from)}
          disabled={periodDisabled}
          onChange={handleDayChange}
          slotProps={pickerSlotProps}
        />
      );
    }
    const { period } = filter;
    const picker = SPAN_PICKERS[period];
    const step = picker.step();
    return (
      <>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(['from', 'to'] as const).map((end) => (
            <DatePicker
              key={`${period}-${end}`}
              format={picker.format}
              views={picker.views}
              openTo={period}
              label={end === 'from' ? i18n._('From') : i18n._('To')}
              value={toDayjs(filter[end])}
              disabled={periodDisabled}
              onChange={handleSpanEnd(period, end)}
              slotProps={pickerSlotProps}
            />
          ))}
        </Box>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, mt: 1,
        }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>{i18n._('Move the end')}</Typography>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={periodDisabled || !canShortenSpan(filter, period)}
            onClick={() => onChange(stepSpanEnd(filter, period, -1))}
          >
            {step.back}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={periodDisabled || !filter.to}
            onClick={() => onChange(stepSpanEnd(filter, period, 1))}
          >
            {step.forward}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <>
      <TextField
        label={i18n._('Spool Dates')}
        size="small"
        fullWidth
        color="secondary"
        value={describeSpoolDateFilter(filter)}
        placeholder={i18n._('All dates')}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
          endAdornment: (
            <InputAdornment position="end">
              {active && (
                <IconButton size="small" aria-label={i18n._('Clear date filter')} onClick={handleClear} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              <ArrowDropDownIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, width: 340 }}>
          <Choice<SpoolDateField>
            label={i18n._('Match on')}
            value={filter.field}
            options={(['inService', 'installed', 'removed'] as SpoolDateField[]).map((field) => ({ value: field, label: spoolDateFieldLabel(field) }))}
            onChange={(field) => onChange(withField(filter, field))}
          />
          <Typography variant="caption" color="text.secondary">{fieldHint(filter.field)}</Typography>

          <Choice<SpoolDatePeriod>
            label={i18n._('Period')}
            value={filter.period}
            disabled={periodDisabled}
            options={[
              { value: 'year', label: i18n._('Year') },
              { value: 'month', label: i18n._('Month') },
              { value: 'day', label: i18n._('Day') },
              { value: 'range', label: i18n._('Range') },
            ]}
            onChange={(period) => onChange(withPeriod(filter, period))}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Box sx={{ mt: 1.5 }}>{renderPeriodPicker()}</Box>
          </LocalizationProvider>

          <Choice<UndatedSpools>
            label={i18n._('Spools with no date')}
            value={filter.undated}
            options={[
              { value: 'show', label: i18n._('Show') },
              { value: 'hide', label: i18n._('Hide') },
              { value: 'only', label: i18n._('Only') },
            ]}
            onChange={(undated) => onChange({ ...filter, undated })}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button size="small" color="secondary" disabled={!active} onClick={() => onChange(NO_SPOOL_DATE_FILTER)}>
              {i18n._('Clear')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default SpoolDateFilterField;
